// AES-256-CTR with a key/IV pair derived from a per-account secret via
// HMAC-SHA256 - a port of the Go backend's scheme (see
// backend/protocol/crypto.go) via the browser's SubtleCrypto, needed to
// speak the "Cue" WebSocket signaling protocol from the frontend (see
// client.ts). Third implementation of this exact scheme in the codebase -
// see ios/zmist/Models/CueCrypto.swift for the Swift port this mirrors.
//
// The IV is no longer deterministic: encryptBase64 folds a fresh 8-byte
// nonce (see currentNonce below) into the "iv"+secret HMAC for every call,
// instead of always signing an empty message - the old scheme signed
// nothing, so the IV (and therefore the ciphertext, for a repeated
// plaintext) never changed, which was an observable weakness, not just a
// theoretical one. The nonce isn't secret, only unique per message, so it
// travels in the clear as the first 8 bytes of the returned blob, before
// base64 - decryptBase64 reads it back out to reconstruct the same IV.
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const HMAC_ALGORITHM: HmacImportParams = { name: 'HMAC', hash: 'SHA-256' };
const NONCE_SIZE = 8;

async function hmacSha256(keyBytes: Uint8Array, message: Uint8Array): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey('raw', keyBytes as BufferSource, HMAC_ALGORITHM, false, ['sign']);
  const signature = await crypto.subtle.sign('HMAC', key, message as BufferSource);
  return new Uint8Array(signature);
}

// key = HMAC-SHA256(key: secret, message: "")[0:32]
async function deriveKey(secret: string): Promise<Uint8Array> {
  const mac = await hmacSha256(textEncoder.encode(secret), new Uint8Array(0));
  return mac.slice(0, 32);
}

// Per-message IV = HMAC-SHA256(key: "iv" + secret, message: nonce)[0:16] -
// see this file's header comment for why nonce replaces the old always-
// empty message.
async function deriveMessageIV(secret: string, nonce: Uint8Array): Promise<Uint8Array> {
  const mac = await hmacSha256(textEncoder.encode(`iv${secret}`), nonce);
  return mac.slice(0, 16);
}

// 6-byte (48-bit) big-endian millisecond unix timestamp, plus 2 random
// tie-breaker bytes (matches backend/protocol/crypto.go's newNonce) - the
// timestamp alone isn't quite enough, since two calls landing in the same
// millisecond would otherwise reuse the same IV, exactly the bug this is
// fixing. Doesn't need to match the Go side's nonce byte-for-byte (each
// side only ever decrypts what it - or another port of this same scheme -
// itself encrypted), just needs to not repeat.
function currentNonce(): Uint8Array {
  const nonce = new Uint8Array(NONCE_SIZE);
  let ms = BigInt(Date.now());
  for (let i = 5; i >= 0; i--) {
    nonce[i] = Number(ms & 0xffn);
    ms >>= 8n;
  }
  crypto.getRandomValues(nonce.subarray(6, 8));
  return nonce;
}

// counter: iv, length: 128 makes SubtleCrypto treat the whole 16-byte IV as
// an incrementing big-endian counter block, matching Go's
// crypto/cipher.NewCTR (which has no separate fixed-nonce prefix - the
// entire IV is the initial counter value).
async function importAesCtrKey(secret: string): Promise<CryptoKey> {
  const keyBytes = await deriveKey(secret);
  return crypto.subtle.importKey('raw', keyBytes as BufferSource, { name: 'AES-CTR' }, false, ['encrypt', 'decrypt']);
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function fromBase64(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export async function encryptBase64(secret: string, plaintext: string): Promise<string> {
  const nonce = currentNonce();
  const key = await importAesCtrKey(secret);
  const iv = await deriveMessageIV(secret, nonce);
  const encryptionAlgorithm = { name: 'AES-CTR', counter: iv as BufferSource, length: 128 };
  const ciphertext = await crypto.subtle.encrypt(encryptionAlgorithm, key, textEncoder.encode(plaintext));
  const blob = new Uint8Array(nonce.length + ciphertext.byteLength);
  blob.set(nonce, 0);
  blob.set(new Uint8Array(ciphertext), nonce.length);
  return toBase64(blob);
}

export async function decryptBase64(secret: string, base64Ciphertext: string): Promise<string> {
  const raw = fromBase64(base64Ciphertext);
  if (raw.length < NONCE_SIZE) {
    throw new Error('ciphertext too short to contain a nonce');
  }
  const nonce = raw.slice(0, NONCE_SIZE);
  const body = raw.slice(NONCE_SIZE);
  const key = await importAesCtrKey(secret);
  const iv = await deriveMessageIV(secret, nonce);
  const encryptionAlgorithm = { name: 'AES-CTR', counter: iv as BufferSource, length: 128 };
  const plaintext = await crypto.subtle.decrypt(encryptionAlgorithm, key, body as BufferSource);
  return textDecoder.decode(plaintext);
}
